import { useState } from 'react'
import { useDataStore } from '../../stores/dataStore'
import { Button, Input, Select, Textarea, Modal } from '../ui'
import { toast } from '../ui/Toast'

interface AddContactModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function AddContactModal({ isOpen, onClose }: AddContactModalProps) {
  const { companies, addContact } = useDataStore()
  
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetForm = () => {
    setName('')
    setEmail('')
    setPhone('')
    setRole('')
    setCompanyId('')
    setNotes('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast('Name is required', 'error')
      return
    }

    setIsSubmitting(true)
    try {
      await addContact({
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        role: role.trim() || undefined,
        companyId: companyId || undefined,
        notes: notes.trim() || undefined,
      })
      toast('Contact created', 'success')
      handleClose()
    } catch {
      toast('Failed to create contact', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const companyOptions = [
    { value: '', label: 'No company' },
    ...companies.map((c) => ({ value: c.id, label: c.name })),
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New contact"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create contact'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Jane Smith"
          autoFocus
        />
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jane@example.com"
        />
        <Input
          label="Phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+44 7700 900000"
        />
        <Input
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="e.g. CTO"
        />
        <Select
          label="Company"
          options={companyOptions}
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
        />
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any additional notes..."
        />
      </div>
    </Modal>
  )
}
